import { Link, useLocation } from '@tanstack/react-router';
import { Sheet, SheetContent, SheetTitle } from '@/components/atoms/sheet';
import { useTotalUnread } from '@/stores/inbox.store';
import {
  useAuthFieldMasks,
  useAuthPermissions,
  useAuthTechnicianId,
  useAuthPermissionScopes,
} from '@/stores/auth.store';
import { useCostingSettings } from '@/hooks/costing/useCostingSettings';
import { useFeatureFlags } from '@/stores/tenant.store';
import {
  NAV_SECTIONS,
  isNonNavRoute,
  PINNED_NAV_ITEMS,
  PINNED_SECTIONS,
  SETTINGS_ITEM,
  SETTINGS_SECTION,
  isSettingsPath,
  isNavItemVisible,
  type NavItem,
} from './nav-config';
import { cn } from '@/lib/utils';
import { MoreHorizontal, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DialerTrigger } from '@/components/voip/Dialer';
import { TEAM_CHAT_PATH, useTeamChatUnreadCount } from '@/components/team-chat/hooks';

const MOBILE_PRIMARY_TAB_LIMIT = 4;
type MoreView = 'main' | 'settings';

function canShowItem(
  item: NavItem,
  permissions: string[],
  technicianId: string | null,
  costingReportingEnabled: boolean,
  flags: Record<string, boolean>,
  fieldMasks: readonly string[],
  permissionScopes: Record<string, string>,
) {
  return isNavItemVisible(item, permissions, {
    technicianId,
    costingReportingEnabled,
    flags,
    fieldMasks,
    permissionScopes,
  });
}

function isNavItemActive(pathname: string, item: NavItem) {
  if (item.activePaths?.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
    return true;
  }
  return item.exact
    ? pathname === item.path
    : pathname === item.path || pathname.startsWith(item.path + '/');
}

function getVisibleNavItems(
  items: NavItem[],
  permissions: string[],
  primaryPaths: Set<string>,
  technicianId: string | null,
  costingReportingEnabled: boolean,
  flags: Record<string, boolean>,
  fieldMasks: readonly string[],
  permissionScopes: Record<string, string>,
) {
  return items
    .filter(
      (item) =>
        !primaryPaths.has(item.path) &&
        canShowItem(
          item,
          permissions,
          technicianId,
          costingReportingEnabled,
          flags,
          fieldMasks,
          permissionScopes,
        ),
    )
    .map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.filter((child) =>
              canShowItem(
                child,
                permissions,
                technicianId,
                costingReportingEnabled,
                flags,
                fieldMasks,
                permissionScopes,
              ),
            ),
          }
        : item,
    )
    .filter((item) => !item.children || item.children.length > 0);
}

function isChildNavItemActive(pathname: string, item: NavItem, siblings: NavItem[]): boolean {
  const childHasQuery = item.path.includes('?');
  if (childHasQuery) return `${pathname}${window.location.search}` === item.path;
  if (item.exact) return pathname === item.path;
  // Same rule as the sidebar: a retired lens keeps its route but no longer has
  // a child to own it, so no shorter sibling may claim it (see NON_NAV_ROUTES).
  if (isNonNavRoute(pathname)) return false;

  const matches = pathname === item.path || pathname.startsWith(item.path + '/');
  if (!matches) return false;

  return !siblings.some(
    (sibling) =>
      sibling.path !== item.path &&
      sibling.path.length > item.path.length &&
      (pathname === sibling.path || pathname.startsWith(sibling.path + '/')),
  );
}

function groupSettingsItems(items: NavItem[]) {
  const groups = new Map<string, NavItem[]>();

  for (const item of items) {
    const group = item.group ?? SETTINGS_SECTION.label;
    groups.set(group, [...(groups.get(group) ?? []), item]);
  }

  return Array.from(groups.entries()).map(([group, groupItems]) => ({
    group,
    items: groupItems,
  }));
}

function MoreNavLink({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const { pathname } = useLocation();
  const active = isNavItemActive(pathname, item);
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
        'active:bg-accent',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

function MoreNavGroup({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const { pathname } = useLocation();
  const isChildActive = isNavItemActive(pathname, item);
  const [open, setOpen] = useState(isChildActive);
  const Icon = item.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
          'active:bg-accent',
          isChildActive ? 'bg-accent/50 text-accent-foreground' : 'text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1 text-left">{item.label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
      </button>
      {open && item.children && (
        <div className="ml-7 space-y-0.5 border-l pl-2">
          {item.children.map((child) => {
            const childActive = isChildNavItemActive(pathname, child, item.children ?? []);
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={onClick}
                className={cn(
                  'flex items-center rounded-md px-3 py-2 text-xs font-medium transition-colors',
                  'active:bg-accent',
                  childActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                )}
              >
                <span className="truncate">{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SettingsNavButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  const Icon = SETTINGS_ITEM.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
        'active:bg-accent',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">{SETTINGS_ITEM.label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

export function MobileBottomBar() {
  const { pathname } = useLocation();
  const totalUnread = useTotalUnread();
  const teamChatUnread = useTeamChatUnreadCount();
  const permissions = useAuthPermissions();
  const fieldMasks = useAuthFieldMasks();
  const technicianId = useAuthTechnicianId();
  const permissionScopes = useAuthPermissionScopes();
  const costingSettings = useCostingSettings();
  const costingReportingEnabled = costingSettings.data?.enableReporting === true;
  const flags = useFeatureFlags();
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreView, setMoreView] = useState<MoreView>('main');

  const visiblePinnedItems = useMemo(
    () =>
      PINNED_NAV_ITEMS.filter((item) =>
        canShowItem(
          item,
          permissions,
          technicianId,
          costingReportingEnabled,
          flags,
          fieldMasks,
          permissionScopes,
        ),
      ),
    [permissions, technicianId, costingReportingEnabled, flags, fieldMasks, permissionScopes],
  );
  const visiblePrimaryTabs = useMemo(
    () =>
      visiblePinnedItems
        .slice(0, MOBILE_PRIMARY_TAB_LIMIT)
        // The dialer ("Call") already lives in the bar, so the Call-logs tab is a
        // redundant phone icon on mobile — drop it here; it stays reachable in "More".
        .filter((item) => item.path !== '/dashboard/calllogs'),
    [visiblePinnedItems],
  );
  const primaryPaths = useMemo(
    () => new Set<string>(visiblePrimaryTabs.map((item) => item.path)),
    [visiblePrimaryTabs],
  );
  const overflowPinnedItems = useMemo(
    () => visiblePinnedItems.filter((item) => !item.children && !primaryPaths.has(item.path)),
    [visiblePinnedItems, primaryPaths],
  );
  const visiblePinnedWorkspaceItems = useMemo(
    () =>
      PINNED_SECTIONS.map<NavItem | null>((section) => {
        const children = section.items.filter((item) =>
          canShowItem(
            item,
            permissions,
            technicianId,
            costingReportingEnabled,
            flags,
            fieldMasks,
            permissionScopes,
          ),
        );
        if (children.length === 0) return null;
        return {
          label: section.label,
          path: children[0].path,
          icon: section.icon,
          activePaths: children.map((child) => child.path),
          children,
        };
      }).filter((item): item is NavItem => Boolean(item)),
    [permissions, technicianId, costingReportingEnabled, flags, fieldMasks, permissionScopes],
  );
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: getVisibleNavItems(
          section.items,
          permissions,
          primaryPaths,
          technicianId,
          costingReportingEnabled,
          flags,
          fieldMasks,
          permissionScopes,
        ),
      })).filter((section) => section.items.length > 0),
    [
      permissions,
      primaryPaths,
      technicianId,
      costingReportingEnabled,
      flags,
      fieldMasks,
      permissionScopes,
    ],
  );
  const settingsItems = useMemo(
    () =>
      SETTINGS_SECTION.items.filter((item) =>
        canShowItem(
          item,
          permissions,
          technicianId,
          costingReportingEnabled,
          flags,
          fieldMasks,
          permissionScopes,
        ),
      ),
    [permissions, technicianId, costingReportingEnabled, flags, fieldMasks, permissionScopes],
  );
  const groupedSettingsItems = useMemo(() => groupSettingsItems(settingsItems), [settingsItems]);
  const settingsActive = isSettingsPath(pathname);

  // Check if current path matches a "More" section (not primary tabs)
  const isMoreActive =
    !visiblePrimaryTabs.some((tab) => isNavItemActive(pathname, tab)) &&
    pathname.startsWith('/dashboard');

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background safe-area-bottom">
        <div className="flex items-center justify-around h-14">
          {visiblePrimaryTabs.map((tab) => {
            const Icon = tab.icon;
            const active = isNavItemActive(pathname, tab);
            const badge =
              tab.path === '/dashboard/inbox'
                ? totalUnread
                : tab.path === TEAM_CHAT_PATH
                  ? teamChatUnread
                  : 0;

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 py-1 transition-colors',
                  active ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {badge > 0 && (
                    <span className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium">{tab.label}</span>
              </Link>
            );
          })}

          <DialerTrigger mobile />

          {/* More button */}
          <button
            type="button"
            onClick={() => {
              setMoreView(settingsActive ? 'settings' : 'main');
              setMoreOpen(true);
            }}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-0.5 py-1 transition-colors',
              isMoreActive ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      <Sheet
        open={moreOpen}
        onOpenChange={(open) => {
          setMoreOpen(open);
          if (!open) setMoreView('main');
          else setMoreView(settingsActive ? 'settings' : 'main');
        }}
      >
        <SheetContent
          side="bottom"
          className="max-h-[85vh] overflow-hidden rounded-t-xl p-0"
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">More options</SheetTitle>
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted" />
          <div className="overflow-hidden">
            <div
              className={cn(
                'flex w-[200%] transition-transform duration-200 ease-out',
                moreView === 'settings' ? '-translate-x-1/2' : 'translate-x-0',
              )}
            >
              <div className="max-h-[calc(85vh-0.75rem)] w-1/2 overflow-y-auto px-2 py-4">
                {overflowPinnedItems.length > 0 && (
                  <div className="mb-2">
                    <p className="mb-1 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                      Pinned
                    </p>
                    <div className="space-y-0.5">
                      {overflowPinnedItems.map((item) => (
                        <MoreNavLink
                          key={item.path}
                          item={item}
                          onClick={() => setMoreOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {visiblePinnedWorkspaceItems.length > 0 && (
                  <div className="mb-2">
                    <p className="mb-1 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                      Workspaces
                    </p>
                    <div className="space-y-0.5">
                      {visiblePinnedWorkspaceItems.map((item) => (
                        <MoreNavGroup
                          key={item.path}
                          item={item}
                          onClick={() => setMoreOpen(false)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {visibleSections.map((section) => (
                  <div key={section.id} className="mb-2">
                    <p className="mb-1 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                      {section.label}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map((item) =>
                        item.children ? (
                          <MoreNavGroup
                            key={item.path}
                            item={item}
                            onClick={() => setMoreOpen(false)}
                          />
                        ) : (
                          <MoreNavLink
                            key={item.path}
                            item={item}
                            onClick={() => setMoreOpen(false)}
                          />
                        ),
                      )}
                    </div>
                  </div>
                ))}
                <div className="mt-2 border-t pt-2">
                  <SettingsNavButton
                    active={settingsActive}
                    onClick={() => setMoreView('settings')}
                  />
                </div>
              </div>

              <div className="max-h-[calc(85vh-0.75rem)] w-1/2 overflow-y-auto px-2 py-4">
                <div className="mb-2 flex items-center gap-2 px-1">
                  <button
                    type="button"
                    onClick={() => setMoreView('main')}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors active:bg-accent hover:bg-accent hover:text-foreground"
                    aria-label="Back to menu"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">Settings</p>
                    <p className="truncate text-xs text-muted-foreground">
                      Account and workspace setup
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {groupedSettingsItems.map(({ group, items }) => (
                    <div key={group}>
                      <p className="mb-1 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                        {group}
                      </p>
                      <div className="space-y-0.5">
                        {items.map((item) => (
                          <MoreNavLink
                            key={item.path}
                            item={item}
                            onClick={() => {
                              setMoreOpen(false);
                              setMoreView('main');
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
